import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Reveal } from '@/components/public/anim';
import { GraduationCap, ArrowRight, ExternalLink } from 'lucide-react';

/**
 * Pagina Educational riusabile (Corsi Online / In Sila presenza).
 * Ogni corso può avere un link INTERNO (`to`, pagina dettaglio con checkout Stripe)
 * oppure un link ESTERNO (`href`, fallback legacy).
 */
export function EducationalPage({ eyebrow, title, lead, hero, courses = [], path, emptyLabel }) {
  return (
    <PublicLayout>
      <Seo title={title} description={lead} path={path} image={hero} />
      <section className="relative isolate flex min-h-[52vh] items-end overflow-hidden text-white" data-testid="educational-hero">
        <img src={hero} alt={title} className="absolute inset-0 -z-10 h-full w-full object-cover" />
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.4) 0%, hsl(150 42% 8% / 0.6) 55%, hsl(150 42% 8% / 0.92) 100%)' }} />
        <div className="sila-section w-full pb-14 pt-32">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide backdrop-blur-md">
            <GraduationCap className="h-3.5 w-3.5" />{eyebrow}
          </span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-tight sm:text-5xl">{title}</h1>
          <p className="mt-3 max-w-2xl text-lg text-white/85">{lead}</p>
        </div>
      </section>

      <section className="sila-section py-16">
        {courses.length === 0 ? (
          <Card className="flex flex-col items-center py-16 text-center" data-testid="educational-empty">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-muted"><GraduationCap className="h-7 w-7 text-muted-foreground" /></div>
            <p className="font-medium">{emptyLabel || 'Nessun corso disponibile al momento'}</p>
            <p className="mt-1 text-sm text-muted-foreground">Torna presto: nuovi percorsi sono in arrivo.</p>
          </Card>
        ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((c) => (
            <Reveal key={c.id || c.title}>
              <Card className="flex h-full flex-col overflow-hidden" data-testid="educational-course-card">
                {c.image && (
                  <div className="aspect-[16/10] w-full overflow-hidden">
                    <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-lg font-semibold leading-snug">{c.title}</h3>
                  {c.text && <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{c.text}</p>}
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div>
                      {c.oldPrice && <span className="mr-2 text-sm text-muted-foreground line-through">{c.oldPrice}</span>}
                      {c.price && <span className="font-display text-lg font-bold text-primary">{c.price}</span>}
                    </div>
                    {c.to ? (
                      <Button asChild size="sm">
                        <Link to={c.to} data-testid="educational-course-link">Scopri <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
                      </Button>
                    ) : c.href ? (
                      <Button asChild size="sm" variant="outline">
                        <a href={c.href} target="_blank" rel="noopener noreferrer">Scopri <ExternalLink className="ml-1.5 h-3.5 w-3.5" /></a>
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
        )}
        <div className="mt-12 rounded-2xl bg-primary p-8 text-center text-primary-foreground">
          <h2 className="font-display text-2xl font-bold">Vuoi proporre un corso?</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-primary-foreground/85">Entra nella rete Sila Experiences come formatore o professionista del benessere.</p>
          <Button asChild variant="secondary" className="mt-5"><a href="/partner/registrati">Diventa Partner <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
        </div>
      </section>
    </PublicLayout>
  );
}
